import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";
import { BACKEND_URL } from "@/lib/api";

const STORAGE_KEY_SESSION = "tbm_chat_session_id";
const STORAGE_KEY_MSGS = "tbm_chat_messages";
const WELCOME = {
  role: "assistant",
  content:
    "Hey! I'm Buddy, ThemBoys' assistant. Ask me about pricing, coverage, or how to book — I'll get you a real answer in seconds.",
};

function loadPersisted() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_MSGS);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    }
  } catch (_) {}
  return [WELCOME];
}

function getOrCreateSessionId() {
  let sid = null;
  try {
    sid = localStorage.getItem(STORAGE_KEY_SESSION);
  } catch (_) {}
  if (!sid) {
    sid =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `sess-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    try {
      localStorage.setItem(STORAGE_KEY_SESSION, sid);
    } catch (_) {}
  }
  return sid;
}

const QUICK_ASKS = [
  "How much for a 2BR move?",
  "Do you serve Nashville?",
  "How do I book?",
  "What's the $100 deposit?",
];

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(loadPersisted);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [unread, setUnread] = useState(0);
  const [leadCaptured, setLeadCaptured] = useState(false);
  const sessionIdRef = useRef(getOrCreateSessionId());
  const scrollRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY_MSGS, JSON.stringify(messages));
    } catch (_) {}
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    if (open) setUnread(0);
  }, [open]);

  const send = async (text) => {
    const trimmed = (text ?? input).trim();
    if (!trimmed || sending) return;
    setInput("");
    setSending(true);
    setMessages((m) => [
      ...m,
      { role: "user", content: trimmed },
      { role: "assistant", content: "" }, // placeholder we'll fill via stream
    ]);

    try {
      const controller = new AbortController();
      abortRef.current = controller;
      const res = await fetch(`${BACKEND_URL}/api/chat/message`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: sessionIdRef.current,
          message: trimmed,
        }),
        signal: controller.signal,
      });

      if (!res.ok || !res.body) {
        throw new Error(`Chat failed (${res.status})`);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      // Streaming loop — decode SSE frames
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const raw = line.slice(5).trim();
          if (raw === "[DONE]") continue;
          try {
            const payload = JSON.parse(raw);
            if (payload.delta) {
              setMessages((m) => {
                const copy = [...m];
                const last = copy[copy.length - 1];
                if (last && last.role === "assistant") {
                  copy[copy.length - 1] = {
                    ...last,
                    content: last.content + payload.delta,
                  };
                }
                return copy;
              });
              if (!open) setUnread((u) => u + 1);
            } else if (payload.lead_captured) {
              setLeadCaptured(true);
              setMessages((m) => [
                ...m,
                {
                  role: "system",
                  content: `✓ Saved. One of the boys will reach out to you shortly, ${payload.name?.split(" ")[0] || "friend"}.`,
                },
              ]);
            } else if (payload.error) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = {
                  role: "assistant",
                  content:
                    "Sorry, I hit a snag. Please try again or call us at (502) 507-7904.",
                };
                return copy;
              });
            }
          } catch (_) {
            // ignore malformed frame
          }
        }
      }
    } catch (err) {
      setMessages((m) => {
        const copy = [...m];
        const last = copy[copy.length - 1];
        if (last && last.role === "assistant" && !last.content) {
          copy[copy.length - 1] = {
            role: "assistant",
            content:
              "Sorry, I couldn't reach my brain right now. Please call us at (502) 507-7904.",
          };
        }
        return copy;
      });
    } finally {
      setSending(false);
      abortRef.current = null;
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const reset = () => {
    setMessages([WELCOME]);
    setLeadCaptured(false);
    try {
      localStorage.removeItem(STORAGE_KEY_MSGS);
    } catch (_) {}
  };

  return (
    <>
      {/* Launcher */}
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Open chat"
          className="fixed z-50 bottom-24 right-5 lg:bottom-6 lg:right-6 bg-[#5BB239] hover:bg-[#499828] text-black hover:text-white shadow-2xl w-14 h-14 rounded-full flex items-center justify-center transition-colors group"
          data-testid="chat-launcher"
        >
          <MessageCircle className="w-6 h-6" strokeWidth={2.25} />
          {unread > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center border-2 border-[#F5F3EC]">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
          <span className="absolute right-full mr-3 whitespace-nowrap bg-[#151816] text-white text-xs font-bold uppercase tracking-widest px-3 py-1.5 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
            Ask Buddy
          </span>
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          className="fixed z-50 bottom-24 right-5 lg:bottom-6 lg:right-6 w-[calc(100vw-2.5rem)] sm:w-[380px] h-[560px] max-h-[calc(100vh-8rem)] bg-[#F5F3EC] border-2 border-[#151816] shadow-2xl flex flex-col"
          data-testid="chat-panel"
        >
          {/* Header */}
          <div className="bg-[#151816] text-white px-4 py-3 flex items-center justify-between border-b-2 border-[#5BB239]">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 bg-[#5BB239] flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-black" strokeWidth={2.5} />
              </div>
              <div>
                <div className="font-display font-black text-sm tracking-tight leading-none">
                  Ask Buddy
                </div>
                <div className="font-mono-label text-[9px] text-white/50 tracking-widest mt-0.5">
                  ThemBoys AI · Live
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={reset}
                className="font-mono-label text-[9px] text-white/50 hover:text-white tracking-widest"
                data-testid="chat-reset-btn"
                title="Start a new conversation"
              >
                New
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close chat"
                className="text-white/70 hover:text-white p-1"
                data-testid="chat-close-btn"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto px-4 py-4 space-y-3"
            data-testid="chat-messages"
          >
            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${
                  m.role === "user"
                    ? "justify-end"
                    : m.role === "system"
                    ? "justify-center"
                    : "justify-start"
                }`}
              >
                {m.role === "system" ? (
                  <div
                    className="bg-[#5BB239]/15 border border-[#5BB239] text-[#151816] px-3 py-2 text-xs font-mono-label tracking-wide"
                    data-testid="chat-msg-system"
                  >
                    {m.content}
                  </div>
                ) : (
                  <div
                    className={`max-w-[85%] px-3.5 py-2.5 text-sm leading-snug whitespace-pre-wrap ${
                      m.role === "user"
                        ? "bg-[#151816] text-white"
                        : "bg-white text-[#151816] border border-[#DDD8C9]"
                    }`}
                    data-testid={`chat-msg-${m.role}`}
                  >
                    {m.content || (
                      <span className="inline-flex gap-1 py-1">
                        <span className="w-1.5 h-1.5 bg-[#5BB239] animate-pulse" />
                        <span
                          className="w-1.5 h-1.5 bg-[#5BB239] animate-pulse"
                          style={{ animationDelay: "0.15s" }}
                        />
                        <span
                          className="w-1.5 h-1.5 bg-[#5BB239] animate-pulse"
                          style={{ animationDelay: "0.3s" }}
                        />
                      </span>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Quick-asks (only visible on the welcome screen) */}
          {messages.length === 1 && (
            <div className="px-4 pb-2 flex flex-wrap gap-1.5">
              {QUICK_ASKS.map((q) => (
                <button
                  key={q}
                  type="button"
                  onClick={() => send(q)}
                  className="text-[11px] font-mono-label tracking-widest bg-white border border-[#DDD8C9] hover:border-[#5BB239] px-2.5 py-1.5 text-[#151816]"
                  data-testid={`chat-quick-${q.slice(0, 10)}`}
                >
                  {q}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <div className="border-t-2 border-[#151816] p-3 bg-[#F5F3EC]">
            <div className="flex items-end gap-2">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder="Type your question…"
                rows={1}
                disabled={sending}
                className="flex-1 resize-none bg-white border border-[#DDD8C9] focus:border-[#5BB239] focus:outline-none px-3 py-2.5 text-sm min-h-[42px] max-h-[120px]"
                data-testid="chat-input"
              />
              <button
                type="button"
                onClick={() => send()}
                disabled={sending || !input.trim()}
                aria-label="Send message"
                className="bg-[#5BB239] text-black hover:bg-[#499828] hover:text-white disabled:bg-[#DDD8C9] disabled:text-[#8A8776] disabled:cursor-not-allowed p-2.5 transition-colors"
                data-testid="chat-send-btn"
              >
                <Send className="w-4 h-4" strokeWidth={2.5} />
              </button>
            </div>
            <div className="mt-2 text-center font-mono-label text-[9px] text-[#8A8776] tracking-widest">
              AI-generated · For an official quote call (502) 507-7904
            </div>
          </div>
        </div>
      )}
    </>
  );
}
